import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Axios from 'axios';

import { removeCustomerLog, removeModelLog } from '@libs/NwLogs';

import { ListContext } from '@contexts/ListContext';
import { useStateValue } from '@contexts/StateProvider';
import { UserContext, useUserPermission } from '@contexts/UserContext';

import { forceReloadContact, useArchiveContact, useUnarchiveContact } from '@hooks/API/Contact/UseContact';
import { forceReloadModels } from '@hooks/API/Contact/UseContactsList';

import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';

import ArchiveActionItem from './ArchiveActionItem';
import { ActionsDrawerWrapper } from './ContactDetailsActions-ui';
import ExternalAccessActionItem from './ExternalAccessActionItem';
import ManagementModelArchiveAction from './ManagementModelArchiveAction';
import ModelSubtypeActionItem from './ModelSubtypeActionItem';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const ContactDetailsActions = ({ contact, onClose, fromManagement, fromDrawer, onReloadSearch }) => {
    const [showActions, setShowActions] = useState(false);

    const { mutateAsync: onArchiveContact } = useArchiveContact();
    const { mutateAsync: onUnarchiveContact } = useUnarchiveContact();
    const navigate = useNavigate();

    const { currentUser } = useContext(UserContext);
    const { cachedList } = useContext(ListContext);
    const { nwAgId } = currentUser.ActiveContext;
    const [{ areaContext }] = useStateValue();
    const canGrantExternalAccess = useUserPermission('GrantExternalAccess');
    const canAccessDataUpdateRequest = useUserPermission('DataUpdateRequest');

    const isModel = contact.ContactType === 'Model';
    const isAccounting = areaContext.area === 'accounting';
    const isManagementModel = areaContext.area === 'management' && isModel;
    const tenantNetworkAgencyCount = (cachedList?.accountingGroups?.items || []).reduce(
        (count, group) => count + (group.NetworkAgencies || []).length,
        0
    );
    const isSingleTenantAgency = tenantNetworkAgencyCount === 1;
    const isInBooking = fromManagement ? contact.ST === 4 : contact.SubType === 4;
    const defaultContactEmail = contact?.DefaultConnection?.Email || contact?.E || '';

    const getContactListPath = () => {
        if (areaContext.area === 'management') {
            return contact.ContactType === 'Model' ? '/management-models' : '/management-customers';
        }

        return contact.ContactType === 'Model' ? '/models' : '/customers';
    };

    const handleShowContactActions = () => {
        setShowActions(true);
    };

    const handleCloseContactActions = () => {
        if (onClose) {
            onClose();
        }
        setShowActions(false);
    };

    const isContactArchived = () => {
        if (fromManagement) {
            if (contact.NWAGS) {
                return contact.NWAGS[0].StorageBooking;
            }
        }
        if (contact.NetworkAgencies && contact.NetworkAgencies.length > 0) {
            return contact.NetworkAgencies[0].StorageBooking;
        }
        return false;
    };

    const handleArchiveContact = async () => {
        try {
            await onArchiveContact({
                contactId: contact.ID,
                networkAgencyId: nwAgId,
                contactType: contact.ContactType,
                area: areaContext.area,
            });
            if (contact.ContactType === 'Model') {
                removeModelLog(contact.ID, areaContext.area);
            } else {
                removeCustomerLog(contact.ID, areaContext.area);
            }
            if (onReloadSearch) {
                onReloadSearch();
                handleShowContactActions();
            } else if (!onClose) {
                if (!fromDrawer) {
                    navigate(getContactListPath());
                } else {
                    handleCloseContactActions();
                }
            } else {
                handleCloseContactActions();
            }
        } catch (error) {
            console.error('Store Contact Error => ', error);
        }
    };

    const handleUnarchiveContact = async () => {
        try {
            await onUnarchiveContact({
                contactId: contact.ID,
                networkAgencyId: nwAgId,
                contactType: contact.ContactType,
                area: areaContext.area,
            });
        } catch (error) {
            console.error('Unstore Contact Error => ', error);
        }
    };

    const handleMoveToBooking = async () => {
        await Axios.put('/models/setsubtype', { ModelID: contact.ID, SubType: 'Booking' });
        forceReloadModels();
        forceReloadContact(contact.ID);
    };

    const handleMoveToScouting = async () => {
        await Axios.put('/models/setsubtype', { ModelID: contact.ID, SubType: 'Scouting' });
        forceReloadModels();
        forceReloadContact(contact.ID);
    };

    if (isAccounting && !isModel) {
        return null;
    }

    return (
        <>
            <NwIcon icon={byPrefixAndName.fasl['gear']} size="lg" pointer onClick={handleShowContactActions} />
            {showActions && (
                <NwDrawer title={'Actions'} onClose={handleCloseContactActions}>
                    <ActionsDrawerWrapper>
                        {isAccounting ? null : isManagementModel && !isSingleTenantAgency ? (
                            <ManagementModelArchiveAction contact={contact} />
                        ) : (
                            <ArchiveActionItem
                                archived={isContactArchived()}
                                contactType={contact.ContactType}
                                onArchive={handleArchiveContact}
                                onUnarchive={handleUnarchiveContact}
                            />
                        )}
                        {isModel && (
                            <ModelSubtypeActionItem
                                isInBooking={isInBooking}
                                onMoveToBooking={handleMoveToBooking}
                                onMoveToScouting={handleMoveToScouting}
                            />
                        )}
                        {isModel && (
                            <ExternalAccessActionItem
                                canAccessDataUpdateRequest={canAccessDataUpdateRequest}
                                canGrantExternalAccess={canGrantExternalAccess}
                                contactId={contact.ID}
                                defaultEmail={defaultContactEmail}
                                onRequestClose={handleCloseContactActions}
                            />
                        )}
                    </ActionsDrawerWrapper>
                </NwDrawer>
            )}
        </>
    );
};

export default ContactDetailsActions;
